import { useState, useCallback } from "react";

// Shared, persisted reader appearance + guide settings. All values live in the
// single `reading_os_prefs` localStorage blob so they survive reloads and apply
// across every document. The reader theme is independent of the app theme.

const PREFS_KEY = "reading_os_prefs";
const load = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}; } catch (e) { return {}; } };
const save = (patch) => { try { localStorage.setItem(PREFS_KEY, JSON.stringify({ ...load(), ...patch })); } catch (e) {} };

export const READER_FONTS = [
    { id: "atkinson", label: "Atkinson Hyperlegible", stack: "'Atkinson Hyperlegible', system-ui, sans-serif", note: "dyslexia" },
    { id: "inter", label: "Inter", stack: "'Inter', system-ui, sans-serif", note: "sans" },
    { id: "source", label: "Source Sans 3", stack: "'Source Sans 3', system-ui, sans-serif", note: "sans" },
    { id: "literata", label: "Literata", stack: "'Literata', Georgia, serif", note: "serif" },
    { id: "merriweather", label: "Merriweather", stack: "'Merriweather', Georgia, serif", note: "serif" },
];
export const fontStack = (id) => (READER_FONTS.find((f) => f.id === id) || READER_FONTS[0]).stack;

export const READER_THEMES = ["night", "day", "sepia"];
export const WIDTHS = { narrow: "46ch", standard: "58ch", wide: "72ch" };
export const LINE_HEIGHTS = { compact: 1.35, standard: 1.5, airy: 1.7 };
export const ANCHOR_MODES = ["off", "2", "3", "4", "2l", "3l", "auto"];

export const DEFAULTS = {
    readerTheme: "night",
    readerFont: "atkinson",
    fontSize: 19,
    textWidth: "standard",
    lineHeightMode: "standard",
    wordAnchor: "off",
    anchorIntensity: 0.8,
    autoScroll: true,
    guidedFocus: "trail",   // trail | word | minimal (Guided reading guide style)
    trailLength: "medium",  // short | medium | long (Light-trail fade window)
    textAlign: "left",      // left | justify
};

export function useReaderSettings() {
    const [readerTheme, setThemeState] = useState(() => READER_THEMES.includes(load().readerTheme) ? load().readerTheme : DEFAULTS.readerTheme);
    const [readerFont, setFontState] = useState(() => load().readerFont || DEFAULTS.readerFont);
    const [fontSize, setSizeState] = useState(() => load().fontSize || DEFAULTS.fontSize);
    const [textWidth, setWidthState] = useState(() => load().textWidth || DEFAULTS.textWidth);
    const [lineHeightMode, setLhState] = useState(() => load().lineHeightMode || DEFAULTS.lineHeightMode);
    const [wordAnchor, setAnchorState] = useState(() => load().wordAnchor || DEFAULTS.wordAnchor);
    const [anchorIntensity, setAnchorIntState] = useState(() => load().anchorIntensity ?? DEFAULTS.anchorIntensity);
    const [autoScroll, setAutoScrollState] = useState(() => load().autoScroll ?? DEFAULTS.autoScroll);
    // Respect an explicitly saved choice; otherwise use the new default.
    const [guidedFocus, setGuidedFocusState] = useState(() => load().guidedFocus || DEFAULTS.guidedFocus);
    const [trailLength, setTrailLengthState] = useState(() => load().trailLength || DEFAULTS.trailLength);
    const [textAlign, setTextAlignState] = useState(() => load().textAlign || DEFAULTS.textAlign);

    const setTheme = useCallback((v) => { setThemeState(v); save({ readerTheme: v }); }, []);
    const setFont = useCallback((v) => { setFontState(v); save({ readerFont: v }); }, []);
    const setFontSize = useCallback((v) => { setSizeState(v); save({ fontSize: v }); }, []);
    const setTextWidth = useCallback((v) => { setWidthState(v); save({ textWidth: v }); }, []);
    const setLineHeightMode = useCallback((v) => { setLhState(v); save({ lineHeightMode: v }); }, []);
    const setWordAnchor = useCallback((v) => { setAnchorState(v); save({ wordAnchor: v }); }, []);
    const setAnchorIntensity = useCallback((v) => { setAnchorIntState(v); save({ anchorIntensity: v }); }, []);
    const setAutoScroll = useCallback((v) => { setAutoScrollState(v); save({ autoScroll: v }); }, []);
    const setGuidedFocus = useCallback((v) => { setGuidedFocusState(v); save({ guidedFocus: v }); }, []);
    const setTrailLength = useCallback((v) => { setTrailLengthState(v); save({ trailLength: v }); }, []);
    const setTextAlign = useCallback((v) => { setTextAlignState(v); save({ textAlign: v }); }, []);

    const reset = useCallback(() => {
        setThemeState(DEFAULTS.readerTheme);
        setFontState(DEFAULTS.readerFont);
        setSizeState(DEFAULTS.fontSize);
        setWidthState(DEFAULTS.textWidth);
        setLhState(DEFAULTS.lineHeightMode);
        setAnchorState(DEFAULTS.wordAnchor);
        setAnchorIntState(DEFAULTS.anchorIntensity);
        setAutoScrollState(DEFAULTS.autoScroll);
        setGuidedFocusState(DEFAULTS.guidedFocus);
        setTrailLengthState(DEFAULTS.trailLength);
        setTextAlignState(DEFAULTS.textAlign);
        save({ ...DEFAULTS });
    }, []);

    return {
        readerTheme, setTheme,
        readerFont, setFont,
        fontSize, setFontSize,
        textWidth, setTextWidth,
        lineHeightMode, setLineHeightMode,
        wordAnchor, setWordAnchor,
        anchorIntensity, setAnchorIntensity,
        autoScroll, setAutoScroll,
        guidedFocus, setGuidedFocus,
        trailLength, setTrailLength,
        textAlign, setTextAlign,
        reset,
        // derived, ready to spread onto ReadingSurface
        surface: {
            fontFamily: fontStack(readerFont),
            fontSize: `${fontSize}px`,
            maxWidth: WIDTHS[textWidth] || WIDTHS.standard,
            lineHeight: LINE_HEIGHTS[lineHeightMode] || LINE_HEIGHTS.standard,
            anchorMode: wordAnchor,
            anchorIntensity,
            textAlign,
        },
    };
}
